import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { schoolsAPI } from '../../utils/api';
import SchoolNotFound from '../../components/common/SchoolNotFound';

const CATEGORIES = [
  { icon: '🎒', label: 'School Kits', path: '/kits', color: '#dbeafe' },
  { icon: '📚', label: 'Individual Supplies', path: '/products', color: '#d1fae5' },
  { icon: '🎁', label: 'Return Gifts', path: '/products?category=return-gifts', color: '#fce7f3' },
  { icon: '📂', label: 'Organizers', path: '/products?category=organizers', color: '#fef3c7' },
  { icon: '🔬', label: 'Activity Kits', path: '/products?category=activity-kits', color: '#ede9fe' },
  { icon: '🎨', label: 'Art & Craft', path: '/products?category=art-craft', color: '#ffedd5' },
];

const HomePage = () => {
  const [search, setSearch] = useState('');
  const [schools, setSchools] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dropRef = useRef();

  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (search.length < 2) { setSchools([]); return; }
    setLoading(true);
    const t = setTimeout(() => {
      schoolsAPI.getAll(search)
        .then(r => { setSchools(r.data); setShowDropdown(true); })
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const selectSchool = (school) => {
    setSearch('');
    setShowDropdown(false);
    navigate(`/schools/${school.id}`);
  };

  return (
    <div>
      {/* Hero */}
      <section style={styles.hero}>
        <div className="container text-center" style={{ paddingTop: '3rem', paddingBottom: '3rem' }}>
          <h1 style={styles.heroTitle}>Everything Your Child Needs for School 🎒</h1>
          <p style={styles.heroSub}>Find complete school kits for your school & grade, or shop individually</p>

          {/* Search */}
          <div ref={dropRef} style={{ position: 'relative', maxWidth: 560, margin: '2rem auto 0' }}>
            <div style={styles.searchBox}>
              <span style={{ fontSize: '1.2rem' }}>🏫</span>
              <input
                style={styles.searchInput}
                placeholder="Search your school name..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                onFocus={() => schools.length && setShowDropdown(true)}
              />
              {loading && <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>...</span>}
            </div>

            {showDropdown && schools.length > 0 && (
              <div style={styles.dropdown}>
                {schools.map(s => (
                  <div key={s.id} style={styles.dropdownItem} onClick={() => selectSchool(s)}>
                    <div style={{ fontWeight: 700 }}>{s.name}</div>
                    <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>{s.address}, {s.city}</div>
                  </div>
                ))}
              </div>
            )}
            {search.length >= 2 && !loading && schools.length === 0 && (
              <SchoolNotFound searchTerm={search} />
            )}
          </div>
          <button className="btn btn-primary btn-lg" style={{ marginTop: '1rem' }} onClick={() => navigate('/kits')}>
            Browse All School Kits
          </button>
        </div>
      </section>

      {/* Categories */}
      <section className="container" style={{ padding: '2.5rem 1rem' }}>
        <h2 style={styles.sectionTitle}>Shop by Category</h2>
        <div className="grid-3" style={{ marginTop: '1.25rem' }}>
          {CATEGORIES.map(cat => (
            <div
              key={cat.label}
              style={{ ...styles.catCard, background: cat.color }}
              onClick={() => navigate(cat.path)}
            >
              <span style={{ fontSize: '2.4rem' }}>{cat.icon}</span>
              <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>{cat.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Why us */}
      <section style={{ background: '#fff', padding: '2.5rem 0' }}>
        <div className="container">
          {/* Can't find your school */}
          <div style={{ marginBottom: '2rem' }}>
            <SchoolNotFound />
          </div>
          <h2 style={styles.sectionTitle}>Why Parents Love Us</h2>
          <div className="grid-4" style={{ marginTop: '1.25rem' }}>
            {[
              { icon: '🎯', title: 'School-Specific Kits', desc: 'Kits tailored for your exact school & grade' },
              { icon: '✏️', title: 'Customizable', desc: 'Adjust quantities or remove items you already have' },
              { icon: '💰', title: 'Best Prices', desc: 'Bundles are cheaper than buying individually' },
              { icon: '🚚', title: 'Fast Delivery', desc: 'Delivered before school starts' },
            ].map(f => (
              <div key={f.title} className="card text-center" style={{ padding: '1.5rem 1rem' }}>
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>{f.icon}</div>
                <div style={{ fontWeight: 700, marginBottom: '0.3rem' }}>{f.title}</div>
                <div className="text-muted text-sm">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

const styles = {
  hero: { background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)', color: '#fff' },
  heroTitle: { fontSize: 'clamp(1.5rem, 4vw, 2.4rem)', fontWeight: 900, lineHeight: 1.2 },
  heroSub: { fontSize: '1rem', opacity: 0.88, marginTop: '0.75rem' },
  searchBox: {
    display: 'flex', alignItems: 'center', gap: '0.6rem',
    background: '#fff', borderRadius: 12, padding: '0.7rem 1.1rem',
    boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
  },
  searchInput: { flex: 1, border: 'none', outline: 'none', fontSize: '1rem', color: '#111' },
  dropdown: {
    position: 'absolute', top: '110%', left: 0, right: 0,
    background: '#fff', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
    zIndex: 50, maxHeight: 280, overflowY: 'auto',
  },
  dropdownItem: { padding: '0.75rem 1rem', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', transition: 'background 0.12s', color: '#111827' },
  sectionTitle: { fontSize: '1.4rem', fontWeight: 800 },
  catCard: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    gap: '0.6rem', padding: '1.5rem', borderRadius: 14, cursor: 'pointer',
    transition: 'transform 0.18s, box-shadow 0.18s',
    boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
  },
};

export default HomePage;
